/* eslint id-length: "off" */
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBackwardStep,
  faForwardStep,
  faPause,
  faPlay,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";
import {fmtSeconds} from "@/lib/music/format";

const MDMusicControls = ({
  currentTime,
  duration,
  hasNext = false,
  hasPrev = false,
  isLoading,
  isPlaying,
  volume,
  onNext,
  onPrev,
  onSeekCommit,
  onSeekInput,
  onToggle,
  onVolume,
}) => {
  const hasDuration = duration > 0;
  // Until the audio reports its real length, the seekbar stays inert.
  // The end time then reads "--:--" (preload="none" until first play).
  const seekMax = hasDuration ? duration : 1;
  const startLabel = hasDuration ? fmtSeconds(currentTime) : "--:--";
  const endLabel = hasDuration ? fmtSeconds(duration) : "--:--";
  const playLabel = isPlaying ? "Pause" : "Play";
  const playIcon = isPlaying ? faPause : faPlay;
  const volumeIcon = volume === 0 ? faVolumeXmark : faVolumeHigh;
  const hasTransport = Boolean(onPrev && onNext);
  return (
    <div className="mdmusic-controls">
      <div className="mdmusic-transport">
        {hasTransport && (
          <button aria-label="Previous track"
                  className="mdmusic-transport-btn"
                  disabled={!hasPrev}
                  type="button"
                  onClick={onPrev}
          >
            <span className="icon"><FontAwesomeIcon icon={faBackwardStep}/></span>
          </button>
        )}
        <button aria-label={playLabel}
                className={`mdmusic-play${isLoading ? " is-loading" : ""}`}
                disabled={isLoading}
                type="button"
                onClick={onToggle}
        >
          <span className="icon"><FontAwesomeIcon icon={playIcon}/></span>
        </button>
        {hasTransport && (
          <button aria-label="Next track"
                  className="mdmusic-transport-btn"
                  disabled={!hasNext}
                  type="button"
                  onClick={onNext}
          >
            <span className="icon"><FontAwesomeIcon icon={faForwardStep}/></span>
          </button>
        )}
      </div>
      <div className="mdmusic-progress">
        <div className="mdmusic-seek-row">
          <span className="is-size-7 has-text-grey mdmusic-time">
            {startLabel}
          </span>
          <input aria-label="Seek"
                 className="mdmusic-seek"
                 disabled={!hasDuration}
                 max={seekMax}
                 min={0}
                 step="0.1"
                 type="range"
                 value={Math.min(currentTime, seekMax)}
                 onChange={onSeekInput}
                 onKeyUp={onSeekCommit}
                 onPointerUp={onSeekCommit}
          />
          <span className="is-size-7 has-text-grey mdmusic-time">
            {endLabel}
          </span>
        </div>
        <div className="mdmusic-volume-row is-hidden-mobile">
          <span className="icon is-small"><FontAwesomeIcon icon={volumeIcon}/></span>
          <input aria-label="Volume"
                 className="mdmusic-volume"
                 max={1}
                 min={0}
                 step="0.01"
                 type="range"
                 value={volume}
                 onChange={onVolume}
          />
        </div>
      </div>
    </div>
  );
};

MDMusicControls.propTypes = {
  currentTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  hasNext: PropTypes.bool,
  hasPrev: PropTypes.bool,
  isLoading: PropTypes.bool,
  isPlaying: PropTypes.bool.isRequired,
  volume: PropTypes.number.isRequired,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  onSeekCommit: PropTypes.func.isRequired,
  onSeekInput: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onVolume: PropTypes.func.isRequired,
};

export default MDMusicControls;
